import { Injectable } from '@angular/core';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { FormFieldWrappers } from '../../../gk-kendo/formly';
import { PropertyPriceRegisterFormService } from '../property-price-register-form.service';
import { PprfDictionaryKey } from '../services/pprf-form-dictionary/pprf-form-dictionary.model';
import { PprfFormDictionaryService } from '../services/pprf-form-dictionary/pprf-form-dictionary.service';
import { PprfParcelFormControlName } from './pprf-parcel-form.model';

@Injectable({
  providedIn: 'root',
})
export class PprfParcelFormService {
  constructor(
    private pprfFormDictionaryService: PprfFormDictionaryService,
    private translateService: TranslateService,
    private propertyPriceRegisterFormService: PropertyPriceRegisterFormService,
  ) {}

  getFormConfig(): Observable<{
    fields: FormlyFieldConfig[];
    options?: FormlyFormOptions;
  }> {
    const fields = [
      {
        wrappers: [FormFieldWrappers.FormField],
        key: PprfParcelFormControlName.DevelopmentPlanDesignation,
        type: 'select',
        props: {
          valueProp: 'id',
          labelProp: 'description',
          options: this.pprfFormDictionaryService.getDictionaryByKey(
            PprfDictionaryKey.LandUseMPZP,
          ),
          multiple: true,
        },
      },
      {
        wrappers: [FormFieldWrappers.FormField],
        key: PprfParcelFormControlName.UseMethod,
        type: 'select',
        props: {
          valueProp: 'id',
          labelProp: 'description',
          options: this.pprfFormDictionaryService.getDictionaryByKey(
            PprfDictionaryKey.UsageTypeLand,
          ),
          multiple: true,
        },
      },
      {
        fieldGroupClassName: 'd-md-flex d-block flex-row column-gap-1',
        fieldGroup: [
          {
            className: 'flex-grow-1',
            key: PprfParcelFormControlName.NumberNumerator,
            type: 'number',
            props: {
              min: 0,
            },
          },
          {
            key: PprfParcelFormControlName.Slash,
            className: 'display-6 p-2 d-lg-none d-xxxl-block d-md-block d-none',
            template: `/`,
          },
          {
            className: 'flex-grow-1',
            key: PprfParcelFormControlName.NumberDenominator,
            type: 'number',
            props: {
              min: 0,
            },
          },
          {
            key: PprfParcelFormControlName.Dash,
            className: 'display-6 p-2 d-lg-none d-xxxl-block d-md-block d-none',
            template: `-`,
          },
          {
            className: 'flex-grow-1',
            key: PprfParcelFormControlName.NumberAdditional,
            type: 'input',
          },
        ],
      },
      {
        fieldGroupClassName: 'd-flex flex-row column-gap-1 flex-wrap',
        fieldGroup: [
          {
            className: 'flex-grow-1',
            key: PprfParcelFormControlName.AreaFrom,
            type: 'number',
            props: {
              min: 0,
              maxlength: 10,
              addonRight: {
                text: 'm²',
              },
            },
          },
          {
            className: 'flex-grow-1',
            key: PprfParcelFormControlName.AreaTo,
            type: 'number',
            props: {
              min: 0,
              maxlength: 10,
              addonRight: {
                text: 'm²',
              },
            },
          },
        ],
      },
    ];

    return this.propertyPriceRegisterFormService
      .getFieldsWithCustomConfig(fields, this.getLabel)
      .pipe(
        map((fieldsWithCustomConfig) => {
          return { fields: fieldsWithCustomConfig };
        }),
        catchError((error) => {
          console.error(error);

          return throwError(() => error);
        }),
      );
  }

  getLabel = (controlKey: string): Observable<string> => {
    return this.translateService.stream(
      `PROPERTY_PRICE_REGISTER_FORM.PARCEL.${controlKey.toUpperCase()}`,
    );
  };
}
